import { useRef } from 'react';
import { ActionIcon, Menu, TextInput, Textarea, Tooltip } from '@mantine/core';
import { IconBraces } from '@tabler/icons-react';

/**
 * A text/textarea input with an "Insert variable" menu so a business owner
 * picks a personalization token from a list instead of typing `{{…}}` syntax
 * by hand. Inserts the token at the caret (or appends when unfocused).
 *
 * Token options are built from the flow graph (see `buildTokenOptions`):
 * static `{{contact.*}}` fields plus `{{field.<name>}}` for every capture
 * node in the same flow.
 */

export interface TokenOption {
  /** The literal to insert, e.g. `{{contact.first_name}}`. */
  token: string;
  /** Human label, e.g. "First name". */
  label: string;
  /** Grouping header, e.g. "Contact" or "Captured answers". */
  group: string;
}

/** Static contact tokens + a `{{field.<inputName>}}` per capture node. */
export function buildTokenOptions(
  captureVariables: Array<{ name: string; label?: string }>,
): TokenOption[] {
  const contact: TokenOption[] = [
    { token: '{{contact.first_name}}', label: 'First name', group: 'Contact' },
    { token: '{{contact.last_name}}', label: 'Last name', group: 'Contact' },
    { token: '{{contact.full_name}}', label: 'Full name', group: 'Contact' },
    { token: '{{contact.email}}', label: 'Email', group: 'Contact' },
    { token: '{{contact.phone}}', label: 'Phone', group: 'Contact' },
  ];
  const seen = new Set<string>();
  const captured: TokenOption[] = [];
  for (const v of captureVariables) {
    const name = v.name.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    captured.push({
      token: `{{field.${name}}}`,
      label: v.label?.trim() || name,
      group: 'Captured answers',
    });
  }
  return [...contact, ...captured];
}

/** The static contact tokens offered everywhere a contact is the recipient. */
const CONTACT_TOKENS: TokenOption[] = [
  { token: '{{contact.first_name}}', label: 'First name', group: 'Contact' },
  { token: '{{contact.last_name}}', label: 'Last name', group: 'Contact' },
  { token: '{{contact.full_name}}', label: 'Full name', group: 'Contact' },
  { token: '{{contact.email}}', label: 'Email', group: 'Contact' },
  { token: '{{contact.phone}}', label: 'Phone', group: 'Contact' },
];

/**
 * Token options for manual sends (chat / campaigns) where the recipient is a
 * real contact: the static contact fields + a `{{field.<inputName>}}` entry
 * for every custom field defined on the vendor. Sibling of `buildTokenOptions`
 * (which sources `field.*` from a flow's capture nodes instead).
 */
export function buildContactTokenOptions(
  customFields: Array<{ inputName: string }>,
): TokenOption[] {
  const seen = new Set<string>();
  const fields: TokenOption[] = [];
  for (const f of customFields) {
    const name = f.inputName.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    fields.push({ token: `{{field.${name}}}`, label: name, group: 'Custom fields' });
  }
  return [...CONTACT_TOKENS, ...fields];
}

export function TokenTextInput({
  label,
  description,
  placeholder,
  value,
  onChange,
  onBlur,
  options,
  multiline,
  size = 'sm',
}: {
  label?: string;
  description?: string;
  placeholder?: string;
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  options: TokenOption[];
  multiline?: boolean;
  size?: string;
}) {
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  const insert = (token: string) => {
    const el = ref.current;
    // Insert at the caret when the field is focused; otherwise append.
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange(next);
    // Restore the caret just after the inserted token on the next tick.
    requestAnimationFrame(() => {
      if (!el) return;
      const pos = start + token.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const groups = Array.from(new Set(options.map((o) => o.group)));
  const rightSection = (
    <Menu position="bottom-end" withinPortal shadow="md">
      <Menu.Target>
        <Tooltip label="Insert variable">
          <ActionIcon variant="subtle" size="sm" aria-label="Insert variable">
            <IconBraces size={14} />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown mah={320} style={{ overflowY: 'auto' }}>
        {groups.map((g) => (
          <div key={g}>
            <Menu.Label>{g}</Menu.Label>
            {options
              .filter((o) => o.group === g)
              .map((o) => (
                <Menu.Item key={o.token} onClick={() => insert(o.token)}>
                  {o.label}
                </Menu.Item>
              ))}
          </div>
        ))}
      </Menu.Dropdown>
    </Menu>
  );

  if (multiline) {
    return (
      <Textarea
        ref={ref}
        label={label}
        description={description}
        placeholder={placeholder}
        autosize
        minRows={3}
        size={size}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
        rightSection={rightSection}
        rightSectionProps={{ style: { alignItems: 'flex-start', paddingTop: 6 } }}
      />
    );
  }
  return (
    <TextInput
      ref={ref}
      label={label}
      description={description}
      placeholder={placeholder}
      size={size}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      onBlur={onBlur}
      rightSection={rightSection}
    />
  );
}
